/**
 * Document Intelligence — Documents Master (create / edit).
 *
 * A category and the fields to extract from documents of that category. Each
 * field's description, format and validation criteria are written for the LLM:
 * they are what it reads to find the value and to judge whether it is valid,
 * so the hints under them say so.
 *
 * Field order is kept as entered — it is the order the extracted data is shown
 * in — which is why each field can be moved up and down.
 */
import { useEffect, useRef } from 'react';
import { Controller, useFieldArray, useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Toast } from 'sharedUi/Toast';
import { Button } from 'sharedUi/Button';
import { InputSwitch } from 'sharedUi/InputSwitch';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import PermissionGuard from '../../components/guards/PermissionGuard';
import PageSkeleton from '../../components/common/page-skeleton';
import FormInput from '../../components/forms/form-input';
import FormSelect from '../../components/forms/form-select';
import DiscardChangesDialog from '../../components/dialogs/discard-changes-dialog';
import { Icon } from '../../components/common/icon';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { useNavigationBlocker } from '../../hooks/useNavigationBlocker';
import { JOB_NAMES } from '../../constants/jobNames';
import { extractErrorMessage } from '../../utils/error-handler';
import { documentCategoriesService } from '../../services/document-categories.service';
import {
  DEFAULT_DOCUMENT_LANGUAGE,
  DOCUMENT_FIELD_DATA_TYPES,
  DOCUMENT_LANGUAGES,
  type DocumentCategoryField,
  type DocumentCategoryInput,
} from '../../types/document-intelligence.types';
import { DOCUMENT_MASTER_BASE_PATH } from './paths';

// -----------------------------------------------------------------------------
// Validation (the backend will enforce the same rules)
// -----------------------------------------------------------------------------

const fieldSchema = z.object({
  fieldName: z.string().trim().min(1, 'Field name is required').max(100, 'Must be ≤ 100 characters'),
  dataType: z.enum(DOCUMENT_FIELD_DATA_TYPES as [string, ...string[]], { message: 'Pick a data type' }),
  description: z.string().trim().max(1000, 'Must be ≤ 1000 characters'),
  format: z.string().trim().max(500, 'Must be ≤ 500 characters'),
  validationCriteria: z.string().trim().max(1000, 'Must be ≤ 1000 characters'),
  required: z.boolean(),
  // Declared here or it is lost: zod drops keys its schema does not name, so
  // the switch would look like it worked and the save would store false.
  isSensitive: z.boolean(),
});

const categorySchema = z.object({
  category: z.string().trim().min(1, 'Category name is required').max(150, 'Must be ≤ 150 characters'),
  categoryDescription: z.string().trim().max(1000, 'Must be ≤ 1000 characters'),
  language: z.enum(['en', 'ar'], { message: 'Pick a language' }),
  isActive: z.boolean(),
  fields: z.array(fieldSchema).min(1, 'Add at least one field to extract'),
}).superRefine((value, ctx) => {
  // Field names are how extracted values are keyed, so two with the same name
  // would overwrite each other.
  const seen = new Map<string, number>();
  value.fields.forEach((fld, i) => {
    const key = fld.fieldName.trim().toLowerCase();
    if (!key) return;
    if (seen.has(key)) {
      ctx.addIssue({ code: 'custom', path: ['fields', i, 'fieldName'],
        message: `Duplicate of field ${seen.get(key)! + 1}` });
    } else {
      seen.set(key, i);
    }
  });
});

type CategoryFormData = z.infer<typeof categorySchema>;

const EMPTY_FIELD: DocumentCategoryField = {
  fieldName: '', dataType: 'String', description: '', format: '', validationCriteria: '', required: true,
  isSensitive: false,
};

/** A field as the form holds it. The API always sends isSensitive, but a row
 *  cached from before the flag existed would fail the schema without this. */
const toFormField = (f: DocumentCategoryField): DocumentCategoryField => ({
  ...f, isSensitive: !!f.isSensitive,
});

const DATA_TYPE_OPTIONS = DOCUMENT_FIELD_DATA_TYPES.map((t) => ({ label: t, value: t }));

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const DocumentMasterForm = () => {
  const { id, isReady, navigateToList } = useSecureNavigation({
    entity: 'document-category',
    basePath: DOCUMENT_MASTER_BASE_PATH,
  });
  const isEditMode = Boolean(id);
  const queryClient = useQueryClient();
  const toast = useRef<Toast>(null);

  const {
    control, handleSubmit, reset, formState: { errors, isDirty },
  } = useForm<CategoryFormData>({
    resolver: zodResolver(categorySchema) as Resolver<CategoryFormData>,
    defaultValues: {
      category: '', categoryDescription: '', language: DEFAULT_DOCUMENT_LANGUAGE, isActive: true,
      fields: [{ ...EMPTY_FIELD }],
    },
    mode: 'onChange',
  });
  const { fields, append, remove, move } = useFieldArray({ control, name: 'fields' });

  const { showLeaveDialog, confirmLeave, cancelLeave, handleNavigate } = useNavigationBlocker({ when: isDirty });

  const { data: existing, isLoading: fetchLoading, error: fetchError } = useQuery({
    queryKey: ['document-categories', id],
    queryFn: () => documentCategoriesService.getById(id!),
    enabled: isEditMode && isReady && !!id,
  });
  useBreadcrumbTitle(isEditMode ? existing?.category : null);

  useEffect(() => {
    if (existing && isEditMode) {
      reset({
        category: existing.category,
        categoryDescription: existing.categoryDescription || '',
        language: existing.language || DEFAULT_DOCUMENT_LANGUAGE,
        isActive: existing.isActive,
        fields: existing.fields.map(toFormField),
      });
    }
  }, [existing, isEditMode, reset]);

  const saveMut = useMutation({
    mutationFn: (payload: DocumentCategoryInput) => (isEditMode
      ? documentCategoriesService.update(id!, payload)
      : documentCategoriesService.create(payload)),
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['document-categories'] });
      // Mark clean first, so leaving for the list is not mistaken for
      // abandoning unsaved changes.
      reset({
        category: saved.category,
        categoryDescription: saved.categoryDescription || '',
        language: saved.language || DEFAULT_DOCUMENT_LANGUAGE,
        isActive: saved.isActive,
        fields: saved.fields.map(toFormField),
      });
      toast.current?.show({ severity: 'success', summary: 'Saved', life: 3000,
        detail: `Category "${saved.category}" ${isEditMode ? 'updated' : 'created'}.` });
      setTimeout(() => navigateToList(), 800);
    },
    onError: (err) => {
      toast.current?.show({ severity: 'error', summary: 'Error', life: 10000,
        detail: extractErrorMessage(err, 'Failed to save the category.') });
    },
  });

  const onSubmit = (data: CategoryFormData) => {
    saveMut.mutate({
      category: data.category,
      categoryDescription: data.categoryDescription,
      language: data.language,
      isActive: data.isActive,
      fields: data.fields as DocumentCategoryField[],
    });
  };

  if (isEditMode && fetchLoading) return <PageSkeleton />;

  if (isEditMode && fetchError) {
    return (
      <div className="p-4 flex flex-col items-start gap-3">
        <p className="text-body text-error">{extractErrorMessage(fetchError, 'Failed to load the category.')}</p>
        <Button label="Back to Documents Master" icon="pi pi-arrow-left" severity="secondary" outlined
          onClick={() => navigateToList()} />
      </div>
    );
  }

  const fieldsError = (errors.fields as { message?: string; root?: { message?: string } } | undefined);
  const fieldsMessage = fieldsError?.message || fieldsError?.root?.message;

  return (
    <PermissionGuard jobName={JOB_NAMES.AI_MODEL_MASTER} resourceName="Documents Master">
      <Toast ref={toast} position="top-right" />

      <div className="py-4 px-4 lg:px-6" data-testid="SCR-DocumentIntelligence-Master-Form">
        {/* Header */}
        <div className="mb-4 flex items-center gap-3">
          <button aria-label="Back" type="button"
            onClick={() => handleNavigate(DOCUMENT_MASTER_BASE_PATH)}
            className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-700 transition-colors">
            <i className="pi pi-arrow-left text-lg" />
          </button>
          <div>
            <h1 className="text-title font-semibold text-ink">
              {isEditMode ? 'Edit Document Category' : 'Create Document Category'}
            </h1>
            <p className="text-body text-ink-muted">
              The category, and the fields to extract from documents of that category
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Category */}
          <div className="bg-surface rounded-lg shadow-sm border border-line p-4 mb-3">
            <h2 className="text-heading font-semibold text-ink mb-3">Category</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
              <div className="md:col-span-2">
                <Controller name="category" control={control} render={({ field }) => (
                  <FormInput {...field} value={field.value || ''} label="Category Name" required
                    placeholder="e.g., Transfer Order" error={!!errors.category}
                    helperText={errors.category?.message} />
                )} />
              </div>
              <div>
                <Controller name="language" control={control} render={({ field }) => (
                  <FormSelect name={field.name} value={field.value || DEFAULT_DOCUMENT_LANGUAGE}
                    label="Language" required options={DOCUMENT_LANGUAGES}
                    onChange={(e) => field.onChange(e.target.value || DEFAULT_DOCUMENT_LANGUAGE)}
                    error={!!errors.language}
                    helperText={errors.language?.message || 'The language of the documents in this category.'} />
                )} />
              </div>
              <div className="md:col-span-3">
                <Controller name="categoryDescription" control={control} render={({ field }) => (
                  <FormInput {...field} value={field.value || ''} label="Category Description"
                    multiline rows={2}
                    placeholder="What belongs in this category — e.g., Orders moving an employee from one unit to another."
                    error={!!errors.categoryDescription}
                    helperText={errors.categoryDescription?.message
                      || 'Read by the AI when it decides which category an uploaded document belongs to.'} />
                )} />
              </div>
              <div className="flex items-center gap-2 md:pt-7">
                <Controller name="isActive" control={control} render={({ field }) => (
                  <InputSwitch checked={!!field.value} onChange={(e) => field.onChange(!!e.value)}
                    testId="DocumentMasterForm.Switch.Active" />
                )} />
                <span className="text-body text-ink">Active</span>
                <span className="text-caption text-ink-muted">— only active categories are used to classify uploads</span>
              </div>
            </div>
          </div>

          {/* Fields */}
          <div className="bg-surface rounded-lg shadow-sm border border-line p-4 mb-3">
            <div className="flex items-center justify-between gap-2 mb-1">
              <h2 className="text-heading font-semibold text-ink">
                Fields to Extract <span className="text-body text-ink-muted font-normal">({fields.length})</span>
              </h2>
            </div>
            <p className="text-body-sm text-ink-muted mb-3">
              The description, format and validation criteria are instructions to the AI: say what the
              field is, how it looks (with an example), and what makes a value valid.
            </p>
            {fieldsMessage && <p className="text-body-sm text-error mb-3">{fieldsMessage}</p>}

            <div className="flex flex-col gap-3">
              {fields.map((row, index) => {
                const err = errors.fields?.[index];
                return (
                  <div key={row.id} className="rounded-lg border border-line bg-canvas p-3"
                    data-testid={`DocumentMasterForm.Field.${index}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-label font-medium text-ink">Field {index + 1}</span>
                      <div className="flex items-center gap-1">
                        <button type="button" aria-label={`Move field ${index + 1} up`} disabled={index === 0}
                          onClick={() => move(index, index - 1)}
                          className="p-1.5 rounded text-ink-muted hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30">
                          <Icon name="chevron-down" size={16} className="rotate-180" />
                        </button>
                        <button type="button" aria-label={`Move field ${index + 1} down`} disabled={index === fields.length - 1}
                          onClick={() => move(index, index + 1)}
                          className="p-1.5 rounded text-ink-muted hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30">
                          <Icon name="chevron-down" size={16} />
                        </button>
                        <button type="button" aria-label={`Remove field ${index + 1}`} disabled={fields.length === 1}
                          onClick={() => remove(index)}
                          className="p-1.5 rounded text-ink-muted hover:bg-red-50 hover:text-error dark:hover:bg-red-900/20 disabled:opacity-30"
                          data-testid={`DocumentMasterForm.Field.${index}.Remove`}>
                          <Icon name="trash" size={16} />
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
                      <div className="md:col-span-5">
                        <Controller name={`fields.${index}.fieldName`} control={control} render={({ field }) => (
                          <FormInput {...field} value={field.value || ''} label="Field Name" required
                            placeholder="e.g., Reference Number" error={!!err?.fieldName}
                            helperText={err?.fieldName?.message} />
                        )} />
                      </div>
                      <div className="md:col-span-3">
                        <Controller name={`fields.${index}.dataType`} control={control} render={({ field }) => (
                          <FormSelect name={field.name} value={field.value || ''} label="Data Type" required
                            options={DATA_TYPE_OPTIONS} onChange={(e) => field.onChange(e.target.value)}
                            error={!!err?.dataType} helperText={err?.dataType?.message} />
                        )} />
                      </div>
                      <div className="md:col-span-4 flex flex-wrap items-center gap-x-5 gap-y-2 md:pt-7">
                        <div className="flex items-center gap-2">
                          <Controller name={`fields.${index}.required`} control={control} render={({ field }) => (
                            <InputSwitch checked={!!field.value} onChange={(e) => field.onChange(!!e.value)}
                              testId={`DocumentMasterForm.Field.${index}.Required`} />
                          )} />
                          <span className="text-body text-ink">Required</span>
                        </div>
                        <div className="flex items-center gap-2"
                          title="Personal or confidential data — a name, an ID number, a medical detail">
                          <Controller name={`fields.${index}.isSensitive`} control={control} render={({ field }) => (
                            <InputSwitch checked={!!field.value} onChange={(e) => field.onChange(!!e.value)}
                              testId={`DocumentMasterForm.Field.${index}.Sensitive`} />
                          )} />
                          <span className="text-body text-ink">Sensitive</span>
                        </div>
                      </div>

                      <div className="md:col-span-12">
                        <Controller name={`fields.${index}.description`} control={control} render={({ field }) => (
                          <FormInput {...field} value={field.value || ''} label="Description" multiline rows={2}
                            placeholder="What this field is — e.g., The date the order was issued."
                            error={!!err?.description} helperText={err?.description?.message} />
                        )} />
                      </div>
                      <div className="md:col-span-6">
                        <Controller name={`fields.${index}.format`} control={control} render={({ field }) => (
                          <FormInput {...field} value={field.value || ''} label="Format" multiline rows={2}
                            placeholder="How it looks — e.g., DD-MM-YYYY, or B4/149/2020"
                            error={!!err?.format} helperText={err?.format?.message} />
                        )} />
                      </div>
                      <div className="md:col-span-6">
                        <Controller name={`fields.${index}.validationCriteria`} control={control} render={({ field }) => (
                          <FormInput {...field} value={field.value || ''} label="Validation Criteria" multiline rows={2}
                            placeholder="What makes it valid — e.g., Must be exactly 8 digits."
                            error={!!err?.validationCriteria} helperText={err?.validationCriteria?.message} />
                        )} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-3">
              <Button type="button" label="Add Field" icon="pi pi-plus" severity="secondary" outlined size="small"
                onClick={() => append({ ...EMPTY_FIELD })} testId="DocumentMasterForm.Button.AddField" />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 justify-end">
            <Button type="button" label="Cancel" severity="secondary" outlined
              onClick={() => handleNavigate(DOCUMENT_MASTER_BASE_PATH)} disabled={saveMut.isPending} />
            <Button type="submit"
              label={saveMut.isPending ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
              icon={saveMut.isPending ? 'pi pi-spin pi-spinner' : undefined}
              disabled={saveMut.isPending} testId="DocumentMasterForm.Button.Submit" />
          </div>
        </form>
      </div>

      <DiscardChangesDialog visible={showLeaveDialog} onStay={cancelLeave} onLeave={confirmLeave}
        testId="DocumentMasterForm.Dialog.Leave" />
    </PermissionGuard>
  );
};

export default DocumentMasterForm;
